import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Megaphone, Users, Flame, Inbox, FileCheck, Import, Plus, Rocket } from "lucide-react";
import { ExpressProspectionDialog } from "@/components/ExpressProspectionDialog";

const KPI = ({ icon: Icon, label, value, accent, testId, hint }) => (
  <Card className="kpi-card" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-2 font-display text-3xl font-semibold">{value}</p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </div>
        <div className={`rounded-lg p-2.5 ${accent ? "bg-accent/10 text-accent" : "bg-primary/5 text-primary"}`}>
          <Icon size={20} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [expressOpen, setExpressOpen] = useState(false);

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-6xl space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold sm:text-4xl">Bonjour {user?.first_name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Voici l'état de votre prospection.</p>
        </div>
        <Button asChild variant="outline" data-testid="dashboard-new-campaign-button">
          <Link to="/campagnes/nouvelle"><Plus size={16} className="mr-1.5" /> Nouvelle campagne</Link>
        </Button>
      </div>

      <div className="relative overflow-hidden rounded-xl bg-primary p-6 text-primary-foreground sm:p-8" data-testid="express-banner">
        <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-semibold sm:text-2xl">Démarrer ma prospection en 3 questions</h2>
            <p className="mt-1 max-w-xl text-sm text-white/70">
              Dites ce que vous vendez et où : l'IA trouve des entreprises réelles, rédige les e-mails et lance la séquence. Vous reprenez la main dès qu'un prospect répond.
            </p>
          </div>
          <Button size="lg" onClick={() => setExpressOpen(true)} className="bg-[hsl(39,63%,52%)] text-primary hover:bg-[hsl(39,63%,45%)]" data-testid="express-open-button">
            <Rocket size={18} className="mr-2" /> Démarrer ma prospection
          </Button>
        </div>
      </div>
      <ExpressProspectionDialog open={expressOpen} onClose={() => setExpressOpen(false)} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI icon={Flame} label="Prospects chauds" value={summary?.prospects_chauds ?? 0} accent testId="kpi-hot-prospects" hint="Réponses positives détectées" />
        <KPI icon={Inbox} label="Leads à traiter" value={summary?.reponses_a_traiter ?? 0} accent testId="kpi-replies" hint={`${summary?.devis_demandes ?? 0} devis · ${summary?.appels_demandes ?? 0} appel(s) demandé(s)`} />
        <KPI icon={FileCheck} label="Tâches à faire" value={summary?.taches_a_faire ?? 0} testId="kpi-pending-validation" hint={`${summary?.taches_en_retard ?? 0} en retard · ${summary?.relances_humaines ?? 0} relance(s) humaine(s)`} />
        <KPI icon={Import} label="Prospects en base" value={summary?.prospects_importes ?? 0} testId="kpi-prospects" hint={`${summary?.emails_envoyes ?? 0} e-mail(s) test · ${summary?.reponses_recues ?? 0} réponse(s)`} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KPI icon={Megaphone} label="Campagnes" value={summary?.campagnes_total ?? 0} testId="kpi-campaigns"
          hint={summary ? `${summary.campagnes_validees} validée(s) · ${summary.campagnes_brouillon} brouillon(s)` : ""} />
        <KPI icon={Users} label="Utilisateurs" value={summary?.utilisateurs ?? 0} testId="kpi-users" />
        <Card className="kpi-card">
          <CardContent className="flex h-full flex-col justify-center p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Activité de prospection</p>
            <p className="mt-2 text-sm">
              <span className="font-display text-2xl font-semibold">{summary?.reponses_recues ?? 0}</span> réponse(s) reçue(s)
            </p>
            <p className="text-sm text-muted-foreground">{summary?.emails_envoyes ?? 0} e-mail(s) envoyé(s) en mode test · {summary?.messages_en_attente ?? 0} relance(s) à venir</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-5">
          <h2 className="font-display text-lg font-semibold">Campagnes en cours</h2>
          <div className="mt-3 space-y-2" data-testid="dashboard-campaigns-running">
            {(summary?.campagnes_en_cours || []).length === 0 && <p className="text-sm text-muted-foreground">Aucune campagne validée.</p>}
            {(summary?.campagnes_en_cours || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-md border p-2.5 text-sm">
                <span className="font-medium">{c.nom}</span>
                <Badge variant="secondary">{c.prospects} prospect(s)</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h2 className="font-display text-lg font-semibold">Journal d'activité récent</h2>
          <div className="mt-4 space-y-3" data-testid="dashboard-audit-log">
            {(summary?.journal_recent || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Aucune activité enregistrée.</p>
            )}
            {(summary?.journal_recent || []).map((log) => (
              <div key={log.id} className="flex flex-wrap items-center gap-2 border-b pb-2 text-sm last:border-0">
                <Badge variant="secondary" className="font-normal">{log.action.replace(/_/g, " ")}</Badge>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{log.details}</span>
                <span className="text-xs text-muted-foreground">{log.timestamp ? new Date(log.timestamp).toLocaleString("fr-FR") : ""}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
